import React, { useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  ProviderRouteConfig,
  TenantProviderConfig,
  UpsertRouteRequest,
  buildAvailabilityRules,
  PAYMENT_METHOD_TYPES,
  PAYMENT_METHOD_LABELS,
  PROVIDER_LABELS,
} from "../../model/SitePayment";
import { X } from "lucide-react";

interface Props {
  route: ProviderRouteConfig | null;
  providers: TenantProviderConfig[];
  onClose: (saved: boolean) => void;
}

const PaymentMethodForm: React.FC<Props> = ({ route, providers, onClose }) => {
  const isEdit = !!route;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [paymentMethodType, setPaymentMethodType] = useState(
    route?.payment_method_type || "",
  );
  const [providerName, setProviderName] = useState(route?.provider_name || "");
  const [isEnabled, setIsEnabled] = useState(route?.is_enabled ?? true);
  const [priority, setPriority] = useState(route?.priority ?? 0);
  const [displayName, setDisplayName] = useState(route?.display_name || "");
  const [description, setDescription] = useState(route?.description || "");
  const [icon, setIcon] = useState(route?.icon || "");

  const initialRules = route?.availability_rules ?? [];
  const [requireAuth, setRequireAuth] = useState(
    initialRules.some((r) => r.type === "authenticated"),
  );
  const nifRule = initialRules.find((r) => r.type === "nif");
  const [nifRequirement, setNifRequirement] = useState(
    nifRule ? (nifRule.kind ?? "any") : "",
  );
  const prefixRule = initialRules.find((r) => r.type === "nif_prefix");
  const [nifPrefixes, setNifPrefixes] = useState(
    (prefixRule?.prefixes ?? []).join(","),
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: UpsertRouteRequest = {
        payment_method_type: paymentMethodType,
        provider_name: providerName,
        is_enabled: isEnabled,
        priority,
        display_name: displayName || undefined,
        description: description || undefined,
        icon: icon || undefined,
        availability_rules: buildAvailabilityRules(
          requireAuth,
          nifRequirement,
          nifPrefixes,
        ),
      };
      await api.put(API_URLS.PAYMENT_ROUTES, body);
      onClose(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar.");
    } finally {
      setSaving(false);
    }
  };

  const enabledProviders = providers.filter((p) => p.is_enabled);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">
            {isEdit ? "Editar Metodo" : "Novo Metodo de Pagamento"}
          </h3>
          <button onClick={() => onClose(false)}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div className="select is-fullwidth">
            <select
              value={paymentMethodType}
              onChange={(e) => setPaymentMethodType(e.target.value)}
              disabled={isEdit}
            >
              <option value="">Selecionar...</option>
              {PAYMENT_METHOD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {PAYMENT_METHOD_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <div className="select is-fullwidth">
            <select
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              disabled={isEdit}
            >
              <option value="">Selecionar...</option>
              {enabledProviders.map((p) => (
                <option key={p.provider_name} value={p.provider_name}>
                  {PROVIDER_LABELS[p.provider_name] || p.provider_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome de Exibicao
            </label>
            <input
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Cartao Visa/Mastercard"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descricao
            </label>
            <textarea
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Pagamento seguro via cartao de credito/debito"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icone (URL ou identificador)
            </label>
            <input
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="credit-card"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prioridade (maior = preferido)
              </label>
              <input
                type="number"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm mt-6">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                className="w-4 h-4"
              />
              Ativo
            </label>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              Regras de Disponibilidade
            </h4>

            <label className="flex items-center gap-2 text-sm mb-2">
              <input
                type="checkbox"
                checked={requireAuth}
                onChange={(e) => setRequireAuth(e.target.checked)}
                className="w-4 h-4"
              />
              Requer cliente autenticado
            </label>

            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requisito de NIF
              </label>
              <div className="select is-fullwidth">
                <select
                  value={nifRequirement}
                  onChange={(e) => setNifRequirement(e.target.value)}
                >
                  <option value="">Nenhum</option>
                  <option value="any">NIF PT valido</option>
                  <option value="company">NIF de Empresa</option>
                  <option value="individual">NIF Particular</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prefixo(s) de NIF (separados por virgula)
              </label>
              <input
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={nifPrefixes}
                onChange={(e) => setNifPrefixes(e.target.value)}
                placeholder="5, 6, 7, 8, 9"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button
            className="button is-light is-small"
            onClick={() => onClose(false)}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="button is-primary is-small"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodForm;
